import type { ComponentProps, ReactElement } from 'react';
import styled from '@emotion/styled';
import * as tokens from '@tokens';
import { Tab } from './Tab';

export interface TabsNavProps {
  /**
   * List of `TabsNav.Tab` components.
   */
  children: ReactElement<ComponentProps<typeof Tab>, 'Tab'>[];
  /**
   * Styles override.
   */
  className?: string;
}

const TabsContainer = styled('div')`
  padding-left: ${tokens.spacer.x5};
  box-shadow: inset 0 -1px 0px 0px ${tokens.color.ui_border};
`;

/**
 * TabsNav is the navigational part of Tabs.
 * Use it with `Tabs.Tab` to create a list of tab buttons.
 *
 * {@link https://zeroheight.com/76c439891/p/1901d5-tabs Usage guidelines}
 *
 * {@link https://storybook.hotjar.com/?path=/docs/components-tabs Code documentation}
 *
 * @example
 * <Tabs.Nav>
 *  <Tabs.Tab isSelected>Tab 1</Tabs.Tab>
 *  <Tabs.Tab>Tab 2</Tabs.Tab>
 * </Tabs.Nav>
 */
export const TabsNav = ({ children, className }: TabsNavProps): JSX.Element => {
  return (
    // the 'tablist' role shouldn't be applied until a proper support for keyboard is introduced
    <TabsContainer className={className}>{children}</TabsContainer>
  );
};

TabsNav.Tab = Tab;
